import emailjs from '@emailjs/browser';
import html2canvas from 'html2canvas';
import { APP_CONFIG } from "./appConfig";

// EmailJS configuration - you'll need to set up an account at emailjs.com
const EMAILJS_SERVICE_ID = APP_CONFIG.emailjsServiceId;
const EMAILJS_TEMPLATE_ID = APP_CONFIG.emailjsTemplateId;
const EMAILJS_PUBLIC_KEY = APP_CONFIG.emailjsPublicKey;

// Function to capture screenshot of the webpage
export const captureScreenshot = async () => {
  try {
    console.log('Capturing screenshot of the webpage...');
    
    // Use html2canvas to capture the entire page with reduced quality for EmailJS
    const canvas = await html2canvas(document.body, {
      height: window.innerHeight,
      width: window.innerWidth,
      useCORS: true,
      allowTaint: true,
      scale: 0.3, // Further reduced scale for smaller file size
      quality: 0.4 // Further reduced quality for smaller file size
    });
    
    // Convert canvas to blob with lower quality
    const blob = await new Promise(resolve => {
      canvas.toBlob(resolve, 'image/jpeg', 0.2); // JPEG with 20% quality for maximum compression
    });
    
    console.log('Screenshot captured successfully, size:', blob.size, 'bytes');
    return blob;
  } catch (error) {
    console.error('Error capturing screenshot:', error);
    throw error;
  }
};

// Function to send email with screenshot
export const sendScreenshotEmail = async (userEmail, userName, screenshotBlob) => {
  try {
    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      throw new Error('Email service is not configured. Set REACT_APP_EMAILJS_* variables.');
    }
    console.log('Sending screenshot email to:', userEmail);
    
    // Convert blob to base64 for email attachment
    const base64Screenshot = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(screenshotBlob);
    });
    
    // EmailJS template parameters
    const templateParams = {
      to_email: userEmail,
      to_name: userName,
      from_name: 'Slimmer af Team',
      message: `Hi ${userName},\n\nHere's your personalized loan repayment strategy screenshot as requested!\n\n<img src="${base64Screenshot}" alt="Loan Strategy Screenshot" style="max-width: 100%; height: auto;" />\n\nBest regards,\nThe Slimmer af Team`
    };
    
    // Send email using EmailJS
    console.log('Sending email with EmailJS...');
    
    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams,
      EMAILJS_PUBLIC_KEY
    );
    
    console.log('Email sent successfully:', response);
    return response;
  } catch (error) {
    console.error('EmailJS error details:', error);
    console.error('Error status:', error.status);
    console.error('Error text:', error.text);
    console.error('Full error object:', error);
    throw error;
  }
};

// Alternative: Simple email service using a backend endpoint
export const sendEmailViaBackend = async (userEmail, userName, screenshotBlob) => {
  try {
    console.log('Sending email via backend to:', userEmail);
    
    // Convert blob to base64
    const base64Screenshot = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(screenshotBlob);
    });
    
    // Send to your backend endpoint
    const response = await fetch('/api/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: userEmail,
        subject: 'Your Slimmer af Loan Strategy Screenshot',
        html: `
          <h2>Hi ${userName}!</h2>
          <p>Here's your personalized loan repayment strategy screenshot as requested!</p>
          <img src="${base64Screenshot}" alt="Loan Strategy Screenshot" style="max-width: 100%; height: auto;" />
          <p>Best regards,<br>The Slimmer af Team</p>
        `
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to send email');
    }
    
    console.log('Email sent successfully via backend');
    return response;
  } catch (error) {
    console.error('Error sending email via backend:', error);
    throw error;
  }
};

// Main function to capture and send screenshot
export const captureAndSendScreenshot = async (user) => {
  try {
    console.log('Starting screenshot capture and email process...');
    
    // Capture screenshot
    const screenshot = await captureScreenshot();
    
    // Get user email and name
    const userEmail = user?.email;
    const userName = user?.displayName || user?.email?.split('@')[0] || 'User';
    
    if (!userEmail) {
      throw new Error('No user email found');
    }
    
    // Use EmailJS only - no fallback
    console.log('Sending email with EmailJS only...');
    await sendScreenshotEmail(userEmail, userName, screenshot);
    
    console.log('Screenshot sent successfully to:', userEmail);
    return { success: true, email: userEmail };
  } catch (error) {
    console.error('Error in captureAndSendScreenshot:', error);
    throw error;
  }
};
